import { create } from 'zustand';
import type {
  BrandDto,
  CatalogOptionDto,
  CatalogOptionValueDto,
  CatalogOptionValueInput,
  CategoryTreeNode,
  OptionValueInput,
  ProductBlockInput,
  ProductDetailDto,
  ProductSort,
  ProductStatus,
  ProductSummaryDto,
  ProductKind,
} from '@ecom/contracts';
import type { VariantInput } from '@ecom/sdk';
import { useUiStore } from '@/i18n';
import { translate, type MessageKey } from '@/i18n/messages';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { riyalsToHalalas } from '@/lib/price-input';
import type { PlannedUpdate, PlannedVariant } from '@/lib/variant-matrix';

/**
 * Products and categories.
 *
 * The admin listing shows every status — draft and archived included — because
 * the whole point of this screen is managing products the storefront hides.
 */

export interface ProductFilters {
  search: string;
  status: ProductStatus | '';
  categoryId: string;
  /** Riyals as typed; converted on the digit string. */
  minPrice: string;
  maxPrice: string;
  /** '' = any, 'in' = in stock only, 'out' = out of stock only. */
  stock: '' | 'in' | 'out';
  /** The list's order; '' is the API's own, newest first. */
  sort: ProductSort | '';
}

export const EMPTY_PRODUCT_FILTERS: ProductFilters = {
  search: '',
  status: '',
  categoryId: '',
  minPrice: '',
  maxPrice: '',
  stock: '',
  sort: '',
};

/** What a move of several products did: how many went through, and each refusal with its reason. */
export interface BulkProductOutcome {
  done: number;
  failed: { id: string; name: string; message: string }[];
}

/** A product's words in one language. Omitted fields keep what is stored; null clears. */
interface ProductCopyInput {
  name: string;
  /** The plain long description — left out when blocks are sent, which write it (ADR-055). */
  description?: string | null;
  /** The line beside the price, up to 300 characters (ADR-055). */
  shortDescription?: string | null;
}

/** What a product form submits. Bilingual content is required in English. */
export interface ProductInput {
  translations: {
    en: ProductCopyInput;
    ar?: ProductCopyInput;
  };
  /**
   * The long description as blocks (ADR-055): omitted keeps them, null is the
   * theme's standard layout, and a list is the page — the API derives the
   * plain description from it.
   */
  descriptionBlocks?: ProductBlockInput[] | null;
  /** Halalas. The form collects riyals and converts once, before calling. */
  basePrice: number;
  slug?: string;
  status?: ProductStatus;
  categoryId?: string | null;
  brandId?: string | null;
  /** Its own size chart (ADR-072); null clears it. */
  sizeChartId?: string | null;
  /** Search-result copy; falls back to the name and description when unset. */
  metaTitle?: { en: { name: string }; ar?: { name: string } };
  metaDescription?: { en: { name: string }; ar?: { name: string } };
  /** Free-form merchandising labels. */
  /** Kept out of search engines (ADR-095). */
  noindex?: boolean;
  /** Left out of the product feeds (ADR-096). */
  excludeFromFeeds?: boolean;
  /** Days after delivery to remind a buyer to buy it again (ADR-099); null for none. */
  repurchaseDays?: number | null;
  tags?: string[];
  /** Sold by measure (R2): the unit its variants are measured in, or null for by the piece. */
  measureUnit?: 'm' | 'kg' | 'l' | null;
  /** A parcel, files, or a code (ADR-077). */
  kind?: ProductKind;
  /** On pre-order while set, `YYYY-MM-DD` (ADR-079); null takes it off. */
  preorderShipsOn?: string | null;
  preorderLimit?: number | null;
  /** Shows the store's "how much do I need?" table on its page (R15). */
  showsQuantityGuide?: boolean;
  /** "You may also love" (R15): products picked to go with it, in order. */
  relatedProductIds?: string[];
  /** Every category it is listed in, primary first (R3). */
  categoryIds?: string[];
  variants?: {
    sku: string;
    price?: number;
    stock?: number;
    options?: Record<string, string>;
    isDefault?: boolean;
    measureQuantity?: number | null;
    /** A sample — the library's "sample" way of selling (R16, ADR-052). */
    isSample?: boolean;
  }[];
}

interface CatalogState {
  items: ProductSummaryDto[];
  nextCursor: string | null;
  categories: CategoryTreeNode[];
  brands: BrandDto[];
  loading: boolean;
  loadingMore: boolean;
  saving: boolean;
  error: string | null;
  filters: ProductFilters;
  /** How many match the filter, not just this page. */
  total: number | null;

  current: ProductDetailDto | null;
  currentLoading: boolean;

  load: (filters?: Partial<ProductFilters>) => Promise<void>;
  loadMore: () => Promise<void>;
  loadCategories: () => Promise<void>;
  /** Whether the tree has been read once — until then the screen is loading, not empty. */
  categoriesLoaded: boolean;
  /** Why the tree could not be read; kept apart from `error`, which a save sets. */
  categoriesError: string | null;
  loadBrands: () => Promise<void>;
  open: (id: string) => Promise<void>;
  clearCurrent: () => void;
  create: (input: ProductInput) => Promise<ProductDetailDto | null>;
  createCategory: (input: unknown) => Promise<boolean>;
  updateCategory: (id: string, input: unknown) => Promise<boolean>;
  deactivateCategory: (id: string) => Promise<boolean>;
  addVariant: (productId: string, input: VariantInput) => Promise<boolean>;
  updateVariant: (variantId: string, input: VariantInput) => Promise<boolean>;
  removeVariant: (variantId: string) => Promise<boolean>;
  /** A film and, optionally, its poster — uploaded, then attached (R4). */
  addVideo: (
    productId: string,
    files: { video: File; poster?: File | null },
    details: { durationSeconds?: number | null; label?: { en: string; ar?: string } | null },
  ) => Promise<boolean>;
  removeVideo: (videoId: string) => Promise<boolean>;
  /** The category whose hand-picked order is open, and its products in that order (R3). */
  categoryOrder: { categoryId: string; items: ProductSummaryDto[] } | null;
  openCategoryOrder: (categoryId: string) => Promise<void>;
  saveCategoryOrder: (categoryId: string, productIds: string[]) => Promise<boolean>;
  closeCategoryOrder: () => void;
  /** Labels, swatches and order for the product's option values (R1). */
  setOptionValues: (productId: string, values: OptionValueInput[]) => Promise<boolean>;
  addImage: (productId: string, file: File) => Promise<boolean>;
  removeImage: (productId: string, imageId: string) => Promise<boolean>;
  /** Links an image to a colour (any variant of it), or with null makes it a general one; or sets its alt text. */
  updateImage: (imageId: string, patch: { variantId?: string | null; alt?: string | null }) => Promise<boolean>;
  reorderImages: (productId: string, imageIds: string[]) => Promise<boolean>;
  update: (id: string, input: Partial<ProductInput>) => Promise<boolean>;
  /** A move of several products from the list is in flight. */
  updating: boolean;
  /**
   * Moves products to one status — publish, back to draft, archive — one at a
   * time through the product's own update, each with its own rules: the API
   * may refuse one and take the rest, and the answer says which.
   */
  setStatusMany: (ids: string[], status: ProductStatus) => Promise<BulkProductOutcome>;
  archive: (id: string) => Promise<boolean>;
  /** A draft copy of the product, opened for editing; null with the reason in `error`. */
  duplicate: (id: string) => Promise<ProductDetailDto | null>;

  /** The options library (ADR-052) — what a product's variants pick from. */
  libraryOptions: CatalogOptionDto[];
  loadLibraryOptions: () => Promise<void>;
  /** A page of one option's values, searched on the server; an empty page on failure. */
  searchLibraryValues: (
    optionId: string,
    search: string,
    cursor?: string,
  ) => Promise<{ items: CatalogOptionValueDto[]; nextCursor: string | null }>;
  /** A value added to the library from inside a product — a new colour arriving with a new cloth. */
  createLibraryValue: (optionId: string, input: CatalogOptionValueInput) => Promise<CatalogOptionValueDto | null>;
  /**
   * Writes a reviewed plan: existing variants whose options change first, then
   * the new ones, one at a time — each answers with the whole product, so the
   * editor ends on what was actually written even if a SKU is refused midway.
   */
  applyVariantPlan: (productId: string, plan: { updates: PlannedUpdate[]; creates: PlannedVariant[] }) => Promise<boolean>;
  /** Removes several variants — a colour and everything it is sold as. */
  removeVariants: (variantIds: string[]) => Promise<boolean>;
}

const PAGE_SIZE = 20;

export const useCatalogStore = create<CatalogState>((set, get) => ({
  libraryOptions: [],
  items: [],
  nextCursor: null,
  categories: [],
  brands: [],
  loading: false,
  loadingMore: false,
  saving: false,
  error: null,
  filters: EMPTY_PRODUCT_FILTERS,
  total: null,

  current: null,
  currentLoading: false,
  updating: false,
  categoriesLoaded: false,
  categoriesError: null,

  async load(filters) {
    const next = { ...get().filters, ...filters };
    set({ loading: true, error: null, filters: next });

    try {
      const page = await withSession(() => adminApi.catalog.products(toQuery(next, PAGE_SIZE)));

      set({
        items: page.items,
        nextCursor: page.meta.nextCursor,
        total: page.meta.total ?? null,
        loading: false,
      });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load products.') });
    }
  },

  async loadMore() {
    const { nextCursor, loadingMore, filters, items } = get();
    if (!nextCursor || loadingMore) return;

    set({ loadingMore: true });

    try {
      const page = await withSession(() =>
        adminApi.catalog.products({ ...toQuery(filters, PAGE_SIZE), cursor: nextCursor }),
      );

      set({ items: [...items, ...page.items], nextCursor: page.meta.nextCursor, loadingMore: false });
    } catch (error) {
      set({ loadingMore: false, error: errorMessage(error, 'Could not load more products.') });
    }
  },

  async loadBrands() {
    try {
      const page = await withSession(() => adminApi.catalog.brands());
      set({ brands: page.items });
    } catch {
      // A missing brand list must not block the product form: the field is
      // optional, and an empty select is honest about there being none.
    }
  },

  async loadCategories() {
    set({ categoriesError: null });
    try {
      const categories = await withSession(() => adminApi.catalog.categoryTree());
      set({ categories, categoriesLoaded: true });
    } catch (error) {
      // Said, not swallowed: an empty tree read as "no categories yet" invites
      // someone to create the ones that already exist. A tree shown before
      // stays on screen under the message.
      set({ categoriesError: errorMessage(error, say('categoriesLoadFailed')) });
    }
  },

  async open(id) {
    set({ currentLoading: true, error: null });

    try {
      const product = await withSession(() => adminApi.catalog.product(id));
      set({ current: product, currentLoading: false });
    } catch (error) {
      set({ currentLoading: false, error: errorMessage(error, 'Could not load this product.') });
    }
  },

  clearCurrent() {
    set({ current: null });
  },

  async create(input) {
    set({ saving: true, error: null });

    try {
      const product = await withSession(() => adminApi.catalog.createProduct(input));
      set({ saving: false });
      await get().load();
      return product;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not create the product.') });
      return null;
    }
  },

  async update(id, input) {
    set({ saving: true, error: null });

    try {
      const product = await withSession(() => adminApi.catalog.updateProduct(id, input));

      set((state) => ({
        saving: false,
        current: product,
        // Keep the row in the list consistent without refetching the page.
        items: state.items.map((row) =>
          row.id === id ? { ...row, name: product.name, price: product.price, status: product.status } : row,
        ),
      }));
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not save the product.') });
      return false;
    }
  },

  async setStatusMany(ids, status) {
    set({ updating: true, error: null });
    const failed: BulkProductOutcome['failed'] = [];
    let done = 0;

    // One at a time, like the orders list: each product is its own write with
    // its own refusal, and a burst of parallel writes is how a small server
    // falls over.
    for (const id of ids) {
      try {
        const product = await withSession(() => adminApi.catalog.updateProduct(id, { status }));
        done += 1;
        set((state) => ({
          items: state.items.map((row) => (row.id === id ? { ...row, status: product.status } : row)),
        }));
      } catch (error) {
        const name = get().items.find((row) => row.id === id)?.name ?? id;
        failed.push({ id, name, message: errorMessage(error, say('genericError')) });
      }
    }

    // A view of one status no longer holds what just left it; the rest of the
    // page stays where it is rather than reloading under the pointer.
    set((state) => {
      if (!state.filters.status) return { updating: false };
      const items = state.items.filter((row) => row.status === state.filters.status);
      const gone = state.items.length - items.length;
      return { updating: false, items, total: state.total === null ? null : Math.max(0, state.total - gone) };
    });
    return { done, failed };
  },

  async createCategory(input) {
    return mutate(set, () => adminApi.catalog.createCategory(input), 'create the category').then(
      async (ok) => {
        if (ok) await get().loadCategories();
        return ok;
      },
    );
  },

  async updateCategory(id, input) {
    return mutate(set, () => adminApi.catalog.updateCategory(id, input), 'save the category').then(
      async (ok) => {
        if (ok) await get().loadCategories();
        return ok;
      },
    );
  },

  async deactivateCategory(id) {
    return mutate(set, () => adminApi.catalog.deactivateCategory(id), 'switch off the category').then(
      async (ok) => {
        if (ok) await get().loadCategories();
        return ok;
      },
    );
  },

  /** Each variant call returns the whole product, so the editor stays in step. */
  async addVariant(productId, input) {
    return mutateProduct(set, () => adminApi.catalog.addVariant(productId, input), 'add the variant');
  },

  async updateVariant(variantId, input) {
    return mutateProduct(set, () => adminApi.catalog.updateVariant(variantId, input), 'save the variant');
  },

  async removeVariant(variantId) {
    return mutateProduct(set, () => adminApi.catalog.removeVariant(variantId), 'remove the variant');
  },

  categoryOrder: null,

  async openCategoryOrder(categoryId) {
    set({ categoryOrder: { categoryId, items: [] }, error: null });
    try {
      const items = await withSession(() => adminApi.catalog.categoryProducts(categoryId));
      set({ categoryOrder: { categoryId, items } });
    } catch (error) {
      set({ categoryOrder: null, error: errorMessage(error, 'Could not load the category’s products.') });
    }
  },

  async saveCategoryOrder(categoryId, productIds) {
    set({ saving: true, error: null });
    try {
      const items = await withSession(() => adminApi.catalog.setCategoryOrder(categoryId, productIds));
      set({ saving: false, categoryOrder: { categoryId, items } });
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not save the order.') });
      return false;
    }
  },

  closeCategoryOrder() {
    set({ categoryOrder: null });
  },

  async setOptionValues(productId, values) {
    return mutateProduct(
      set,
      () => adminApi.catalog.setOptionValues(productId, values),
      'save the option labels',
    );
  },

  async addImage(productId, file) {
    set({ saving: true, error: null });

    try {
      // Two steps by design: the file goes to storage, and the returned key is
      // what the product references (ADR-013 — storage is a swappable driver).
      const uploaded = await withSession(() => adminApi.uploads.image(file));
      const product = await withSession(() => adminApi.catalog.addImage(productId, { imageKey: uploaded.key }));
      set({ saving: false, current: product });
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not upload the image.') });
      return false;
    }
  },

  async addVideo(productId, files, details) {
    set({ saving: true, error: null });

    try {
      // The same two steps as an image: storage first, then the product keeps the keys.
      const video = await withSession(() => adminApi.uploads.video(files.video));
      const poster = files.poster ? await withSession(() => adminApi.uploads.image(files.poster as File)) : null;
      const product = await withSession(() =>
        adminApi.catalog.addVideo(productId, {
          videoKey: video.key,
          posterKey: poster?.key ?? null,
          durationSeconds: details.durationSeconds ?? null,
          translations: details.label?.en ? details.label : null,
        }),
      );
      set({ saving: false, current: product });
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not upload the film.') });
      return false;
    }
  },

  async removeVideo(videoId) {
    return mutateProduct(set, () => adminApi.catalog.removeVideo(videoId), 'remove the film');
  },

  async updateImage(imageId, patch) {
    return mutateProduct(set, () => adminApi.catalog.updateImage(imageId, patch), 'update the image');
  },

  async reorderImages(productId, imageIds) {
    return mutateProduct(
      set,
      () => adminApi.catalog.reorderImages(productId, imageIds),
      'reorder the images',
    );
  },

  async removeImage(productId, imageId) {
    set({ saving: true, error: null });

    try {
      await withSession(() => adminApi.catalog.removeImage(imageId));
      const product = await withSession(() => adminApi.catalog.product(productId));
      set({ saving: false, current: product });
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not remove the image.') });
      return false;
    }
  },

  async duplicate(id) {
    set({ saving: true, error: null });
    try {
      const copy = await withSession(() => adminApi.catalog.duplicateProduct(id));
      set({ saving: false, current: copy });
      return copy;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not duplicate the product.') });
      return null;
    }
  },

  async loadLibraryOptions() {
    try {
      const page = await withSession(() => adminApi.options.list());
      set({ libraryOptions: page.items });
    } catch {
      // The picker says there is nothing to pick rather than breaking the editor.
    }
  },

  async searchLibraryValues(optionId, search, cursor) {
    try {
      const page = await withSession(() =>
        adminApi.options.values(optionId, { search: search || undefined, cursor, limit: 48 }),
      );
      return { items: page.items, nextCursor: page.meta.nextCursor };
    } catch {
      return { items: [], nextCursor: null };
    }
  },

  async createLibraryValue(optionId, input) {
    set({ saving: true, error: null });
    try {
      const value = await withSession(() => adminApi.options.createValue(optionId, input));
      set({ saving: false });
      return value;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not add the value.') });
      return null;
    }
  },

  async applyVariantPlan(productId, plan) {
    set({ saving: true, error: null });
    try {
      let product: ProductDetailDto | null = null;
      for (const { variant, options } of plan.updates) {
        product = await withSession(() =>
          adminApi.catalog.updateVariant(variant.id, {
            sku: variant.sku,
            options,
            // The API writes these on every update; they go back as they are.
            price: variant.price === (get().current?.basePrice ?? variant.price) ? null : variant.price,
            isDefault: variant.isDefault,
          }),
        );
        set({ current: product });
      }
      for (const variant of plan.creates) {
        const input = {
          sku: variant.sku,
          options: variant.options,
          price: variant.price,
          stock: 0,
          measureQuantity: variant.measureQuantity,
          maxPerOrder: variant.maxPerOrder,
          weightGrams: variant.weightGrams,
          lengthMm: variant.lengthMm,
          widthMm: variant.widthMm,
          heightMm: variant.heightMm,
          isSample: variant.isSample,
        };
        product = await withSession(() => adminApi.catalog.addVariant(productId, input));
        set({ current: product });
      }
      set({ saving: false });
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not create the variants.') });
      return false;
    }
  },

  async removeVariants(variantIds) {
    set({ saving: true, error: null });
    try {
      for (const variantId of variantIds) {
        const product = await withSession(() => adminApi.catalog.removeVariant(variantId));
        set({ current: product });
      }
      set({ saving: false });
      return true;
    } catch (error) {
      // LAST_VARIANT arrives here with its reason: a product keeps one.
      set({ saving: false, error: errorMessage(error, 'Could not remove the variants.') });
      return false;
    }
  },

  async archive(id) {
    set({ saving: true, error: null });

    try {
      // Archive, never delete: an order's history points at this product.
      await withSession(() => adminApi.catalog.archiveProduct(id));
      set({ saving: false });
      await get().load();
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not archive the product.') });
      return false;
    }
  },
}));

/** Flattens the category tree into indented options for a select. */
export function flattenCategories(
  nodes: CategoryTreeNode[],
  depth = 0,
): { id: string; label: string }[] {
  return nodes.flatMap((node) => [
    { id: node.id, label: `${'— '.repeat(depth)}${node.name}` },
    ...flattenCategories(node.children, depth + 1),
  ]);
}

/** A fallback message in the operator's language, for a failure the API did not word. */
function say(key: MessageKey): string {
  return translate(key, useUiStore.getState().locale);
}

/** Runs a catalog mutation that does not return a product. */
async function mutate(
  set: (partial: Partial<CatalogState>) => void,
  call: () => Promise<unknown>,
  what: string,
): Promise<boolean> {
  set({ saving: true, error: null });

  try {
    await withSession(call);
    set({ saving: false });
    return true;
  } catch (error) {
    set({ saving: false, error: errorMessage(error, `Could not ${what}.`) });
    return false;
  }
}

/** Runs one that returns the product, and adopts it as the open one. */
async function mutateProduct(
  set: (partial: Partial<CatalogState>) => void,
  call: () => Promise<ProductDetailDto>,
  what: string,
): Promise<boolean> {
  set({ saving: true, error: null });

  try {
    const product = await withSession(call);
    set({ saving: false, current: product });
    return true;
  } catch (error) {
    // LAST_VARIANT and duplicate-SKU both arrive here with a usable message.
    set({ saving: false, error: errorMessage(error, `Could not ${what}.`) });
    return false;
  }
}

/**
 * Filters as the API wants them.
 *
 * `status` was declared here and never sent, so choosing one did nothing —
 * a filter that appears to work and does not is worse than one that is missing.
 */
function toQuery(filters: ProductFilters, limit: number) {
  return {
    limit,
    sort: filters.sort || undefined,
    search: filters.search || undefined,
    status: filters.status || undefined,
    categoryId: filters.categoryId || undefined,
    minPrice: riyalsToHalalas(filters.minPrice) ?? undefined,
    maxPrice: riyalsToHalalas(filters.maxPrice) ?? undefined,
    // Only "in stock" is a server filter; "out of stock" is its complement and
    // the API has no flag for it, so it is left to the caller to ask for.
    inStockOnly: filters.stock === 'in' ? true : undefined,
    outOfStockOnly: filters.stock === 'out' ? true : undefined,
  };
}
